import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';

/** A personal token with which a person connects their own agent (Claude, Cursor …) over MCP. */
export interface AgentAccessToken {
  id: string;
  name: string;
  token_prefix: string;
  can_write: boolean;
  expires_at: string | null;
  last_used_at: string | null;
  created_at: string;
  user_id: string;
  user_name?: string | null;
  user_email?: string | null;
  /** 'personal' = a token made here; 'oauth' = a client that signed in (its token renews itself) */
  kind?: 'personal' | 'oauth';
  /** only in the answer to `create` — never stored, never shown again */
  token?: string;
}

/** What a client that wants to sign in asks for (al-user-management `/oauth/requests/{id}`). */
export interface OauthConsentInfo {
  client_name: string;
  client_uri: string | null;
  /** where the answer goes: a web address, this device (localhost) or an app */
  redirect: { kind: 'web' | 'device' | 'app'; target: string };
  wants_write: boolean;
  expires_at: string;
  enabled: boolean;
  user: { name: string | null; email: string | null };
}

/** A provider the portal assistant may run on (only ones fit for its tasks). */
export interface AgentModelProvider {
  provider: string;
  model: string;
  model_label: string;
  model_class: string | null;
  agent_status: string | null;
  credit_multiplier: number;
  speed?: string | null;
}

export interface AgentModelChoice {
  providers: AgentModelProvider[];
  platform_default: { model: string; model_label: string };
  organisation: { provider: string | null; allow_user_choice: boolean };
  me: { provider: string | null };
  effective: AgentModelProvider | null;
  can_manage: boolean;
}

export interface AgentAccessTokenInput {
  name: string;
  expires_in_days: number;
  can_write: boolean;
}

/**
 * Agent-Zugang (al-user-management `/agent-access/tokens`). The MCP server the
 * tokens open is al-tutor `/portal-mcp`; see
 * features/docs/technical/portal-agent-native.md „Eigener Agent (MCP)".
 */
@Injectable({ providedIn: 'root' })
export class AgentAccessService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.userBackend}/agent-access/tokens`;

  /** The address a client connects to. */
  readonly mcpUrl = `${environment.tutorsBackend}/portal-mcp`;

  list(allUsers = false): Observable<{ data: AgentAccessToken[]; enabled: boolean }> {
    return this.http.get<{ data: AgentAccessToken[]; enabled: boolean }>(this.base, {
      params: { all_users: allUsers },
    });
  }

  create(input: AgentAccessTokenInput): Observable<AgentAccessToken> {
    return this.http.post<{ data: AgentAccessToken }>(this.base, input).pipe(map((res) => res.data));
  }

  revoke(id: string): Observable<unknown> {
    return this.http.delete(`${this.base}/${id}`);
  }

  // ---- OAuth sign-in of an agent (consent page /auth/oauth-consent)
  private readonly oauthBase = `${environment.userBackend}/oauth/requests`;

  getConsent(requestId: string): Observable<OauthConsentInfo> {
    return this.http.get<{ data: OauthConsentInfo }>(`${this.oauthBase}/${requestId}`).pipe(map((res) => res.data));
  }

  /** The answer is where the browser goes next: the client's callback (with a code or access_denied). */
  decideConsent(requestId: string, approve: boolean, canWrite: boolean): Observable<{ redirect: string }> {
    return this.http.post<{ redirect: string }>(`${this.oauthBase}/${requestId}`, { approve, can_write: canWrite });
  }

  // ---- model of the portal assistant (al-tutor /portal-agent/model)
  private readonly modelBase = `${environment.tutorsBackend}/portal-agent/model`;

  getModel(): Observable<AgentModelChoice> {
    return this.http.get<{ data: AgentModelChoice }>(this.modelBase).pipe(map((res) => res.data));
  }

  setOrganisationModel(provider: string | null, allowUserChoice: boolean): Observable<AgentModelChoice> {
    return this.http
      .put<{ data: AgentModelChoice }>(`${this.modelBase}/organisation`, { provider, allow_user_choice: allowUserChoice })
      .pipe(map((res) => res.data));
  }

  setMyModel(provider: string | null): Observable<AgentModelChoice> {
    return this.http.put<{ data: AgentModelChoice }>(`${this.modelBase}/me`, { provider }).pipe(map((res) => res.data));
  }
}
